import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { AuthGuard } from './app/guards/auth.guard';
import { AdminGuard } from './app/guards/role.guard';
import { ActivePropertyGuard } from './app/guards/active-property.guard';
import { authInterceptorProviders } from './app/interceptors/auth.interceptor';
import { AuthService } from './app/services/auth.service';
import { TokenStorageService } from './app/services/token-storage.service';
import { mapService } from './app/services/map.service';
import { AccountService } from './app/services/account.service';
import { OfflineService } from './app/services/offline.service';
import { EnterpriseService } from './app/services/enterprise.service';
import { DecimalPipe } from '@angular/common';
import { ActivateComponent } from './app/components/activate/activate.component';
import { ResetPasswordComponent } from './app/components/reset-password/reset-password.component';
import { LoginComponent } from './app/components/login/login.component';
import { MapComponent } from './app/components/map/map.component';
import { RegisterComponent } from './app/components/register/register.component';
import { ForgotPasswordComponent } from './app/components/forgot-password/forgot-password.component';
import { DashboardComponent } from './app/components/dashboard/dashboard.component';
import { AdminComponent } from './app/components/admin/admin.component';
import { FarmWoodlotToolComponent } from './app/components/farm-woodlot-tool/farm-woodlot-tool.component';
import { NotFoundComponent } from './app/not-found/not-found.component';
import { DeleteFenceComponent } from './app/components/map/dialogs/fence/edit-fence/delete-fence/delete-fence.component';
import { DeletePropertyComponent } from './app/components/map/dialogs/property/edit-property/delete-property/delete-property.component';
import { ForestDescriptionDisclaimerOverlayComponent } from './app/components/map/dialogs/forest-description-report/forest-description-disclaimer-overlay/forest-description-disclaimer-overlay.component';
import { ForestDescriptionMapOverlayComponent } from './app/components/map/dialogs/forest-description-report/forest-description-map-overlay/forest-description-map-overlay.component';
import { TopexMapOverlayComponent } from './app/components/map/dialogs/topex-report/topex-map-overlay/topex-map-overlay.component';
import { DssReportComponent } from './app/components/map/dialogs/dss-report/dss-report.component';
import { DssMapOverlayComponent } from './app/components/map/dialogs/dss-report/dss-map-overlay/dss-map-overlay.component';
import { FarmWoodlotToolWelcomeMessageComponent } from './app/components/farm-woodlot-tool/farm-woodlot-tool-welcome-message/farm-woodlot-tool-welcome-message.component';
import { MaterialsSpeciesPlantingComponent } from './app/components/farm-woodlot-tool/materials-species-planting/materials-species-planting.component';
import { TreeProtectionComponent } from './app/components/farm-woodlot-tool/tree-protection/tree-protection.component';
import { SitePreparationGuidelinesComponent } from './app/components/farm-woodlot-tool/site-preparation-guidelines/site-preparation-guidelines.component';
import { IndicativeYieldsComponent } from './app/components/farm-woodlot-tool/indicative-yields/indicative-yields.component';
import { MarketGuidelinesComponent } from './app/components/farm-woodlot-tool/market-guidelines/market-guidelines.component';
import { PtrGuidelinesComponent } from './app/components/farm-woodlot-tool/ptr-guidelines/ptr-guidelines.component';
import { NavComponent } from './app/components/nav/nav.component';

import { ReportCardComponent } from './app/components/map/reporting/report-card/report-card.component';
import { OrdinalPipe } from './app/pipes/ordinal.pipe';
import { SpaceBeforeCapPipe } from './app/pipes/space_before_capitial.pipe';
import { OnlineStatusModule, OnlineStatusService } from 'ngx-online-status';
import { SidebarContentComponent } from './app/components/map/sidebar/sidebar-content.component';


const guards = [AuthGuard];

const routes = [
  {
    path: "woodlot-tool", canActivate: [AuthGuard, ActivePropertyGuard], component: FarmWoodlotToolComponent,
  },
  {
    path: 'dashboard', canActivate: guards, component: DashboardComponent
  },
  {
    path: 'map', canActivate: guards, component: MapComponent
  },
  {
    path: 'map/:tool', canActivate: guards, component: MapComponent
  },
  {
    path: 'login', component: LoginComponent
  },
  {
    path: 'admin',canActivate: [AdminGuard], loadChildren: () => import('./app/@admin/routes').then(m => m.routes),
  },
  {
    path: 'register', component: RegisterComponent
  },
  {
    path: 'account/resetPassword', component: ResetPasswordComponent
  },
  {
    path: 'account/forgotPassword', component: ForgotPasswordComponent
  },
  {
    path: 'account/confirm', component: ActivateComponent
  },
  {
    path: '', redirectTo: 'map', pathMatch: 'full' as const,
  },
  { path: '404', component: NotFoundComponent },
  { path: '**', redirectTo: '/404' }
];

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    { provide: 'OVERLAY_CONTAINER', useValue: document.body },
    OnlineStatusService,
    AuthGuard,
    authInterceptorProviders,
    AuthService,
    TokenStorageService,
    mapService,
    AccountService,
    OfflineService,
    EnterpriseService,
    DecimalPipe,
    DeleteFenceComponent,
    DeletePropertyComponent,
    ForestDescriptionDisclaimerOverlayComponent,
    ForestDescriptionMapOverlayComponent,
    TopexMapOverlayComponent,
    DssReportComponent,
    DssMapOverlayComponent,
    FarmWoodlotToolWelcomeMessageComponent,
    MaterialsSpeciesPlantingComponent,
    TreeProtectionComponent,
    SitePreparationGuidelinesComponent,
    IndicativeYieldsComponent,
    MarketGuidelinesComponent,
    PtrGuidelinesComponent,
    NavComponent,
    ReportCardComponent,
    OrdinalPipe,
    SpaceBeforeCapPipe,
    SidebarContentComponent
  ]
})
  .catch(err => console.error(err));


